import { FormField } from './Form';
import type { FormFieldRenderProps } from './Form';
import { Input, Textarea, Select } from './Input';
import { Checkbox } from './Checkbox';
import { MultiSelect } from './MultiSelect';
import { Rating } from './Rating';
import { DateInput, DateTimeInput } from './DateInput';
import { AddressField } from './AddressField';
import type { AddressValue } from './AddressField';

// DynamicFormField — the ONE per-type renderer for a runtime-built form
// (story 3-1b; extracted from the /app/design "dynamic regime" demo story 2-93
// / 2-96 wrote inline).
//
// G-26 says one primitive absorbs both regimes. Story 2-93 proved that for the
// SCHEMA half (`useZodForm` takes a generated schema or a runtime-assembled
// one). The RENDER half was still a `renderRuntimeField` switch living in the
// gallery page — fine as a proof, a copy-paste invitation the moment a second
// surface needed it. The Sites create/edit form (P03 §5: "rendered from
// `domain.subjectSchema.fields` by the existing input renderer — SAME
// components, one renderer everywhere") is that second surface, so the switch
// moves here and the gallery now demonstrates the shipped component rather
// than a look-alike.
//
// Every branch composes an existing DS primitive through `FormField`, so the
// label/description/error/aria wiring is decided once. `instruction` renders
// static text and no control — it has no value and no schema entry (FormSchema
// v2, agents/shared/formSchema.js FIELD_TYPES).

export interface DynamicFieldSpec {
  key: string;
  type: string;
  label: string;
  required?: boolean | undefined;
  description?: string | undefined;
  placeholder?: string | undefined;
  options?: Array<{ id?: string | undefined; label: string; color?: string | undefined }> | undefined;
  min?: number | undefined;
  max?: number | undefined;
}

export interface DynamicFormFieldProps {
  field: DynamicFieldSpec;
  /** Prefix for the RHF field path, e.g. `attributes` → `attributes.url`. */
  namePrefix?: string;
  disabled?: boolean;
}

/** The option VALUE a select/multiselect writes. Prefers the stable `id` and
 * falls back to the label, matching how response-set options are authored. */
const optionValue = (option: { id?: string | undefined; label: string }): string => option.id ?? option.label;

/**
 * A native `<input type="number">`'s change event always carries a STRING
 * (`event.target.value`), and RHF's `useController` field value does not get
 * coerced by anything downstream just because the resolver's own schema is a
 * `z.number()` — the resolver only re-parses on submit/validate, but the RAW
 * RHF field value is what every OTHER reader sees in between: live
 * min/max-driven evaluation, and (the bug this fixes) the capture screen's
 * autosave, which reads the raw form value straight off `form.watch()` and
 * ships it in the PATCH payload untouched. Spreading `{...f}` straight onto
 * the input, as every other `FormField` control here does, left that value a
 * string all the way to the wire, where the backend's `z.number()` contract
 * rejects it.
 *
 * Kept as the raw STRING while it does not round-trip through `Number(...)`
 * (mid-typing `"-"`, `"4."`, a trailing `"4.50"`) — coercing those eagerly
 * would restringify the DISPLAYED value back to its canonical form on every
 * keystroke (`"4."` → `4` → `"4"`) and silently eat the character just
 * typed. The moment a typed string DOES round-trip it becomes a real number
 * immediately (not deferred to submit), so live validation sees the real
 * value too. Empty clears to `undefined`, never `NaN`.
 */
function numberFieldValue(raw: string): string | number | undefined {
  if (raw === '') return undefined;
  const parsed = Number(raw);
  return Number.isNaN(parsed) || String(parsed) !== raw ? raw : parsed;
}

export function DynamicFormField({ field, namePrefix, disabled }: DynamicFormFieldProps): JSX.Element {
  const name = namePrefix ? `${namePrefix}.${field.key}` : field.key;
  const required = Boolean(field.required);
  const options = field.options ?? [];
  const common = {
    name,
    label: field.label,
    required,
    ...(field.description ? { description: field.description } : {}),
  };

  switch (field.type) {
    case 'instruction':
      return (
        <p key={field.key} className="rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
          {field.label}
        </p>
      );
    case 'textarea':
      return (
        <FormField {...common}>
          {(f) => <Textarea rows={3} placeholder={field.placeholder ?? ''} disabled={disabled ?? false} {...f} />}
        </FormField>
      );
    case 'number':
      return (
        <FormField {...common}>
          {(f) => (
            <Input
              type="number"
              {...(typeof field.min === 'number' ? { min: field.min } : {})}
              {...(typeof field.max === 'number' ? { max: field.max } : {})}
              placeholder={field.placeholder ?? ''}
              disabled={disabled ?? false}
              {...f}
              onChange={(event) => f.onChange(numberFieldValue(event.target.value))}
            />
          )}
        </FormField>
      );
    case 'boolean':
      return (
        <FormField {...common} control="inline">
          {(f: FormFieldRenderProps<boolean>) => <Checkbox disabled={disabled ?? false} {...f} />}
        </FormField>
      );
    case 'select':
      return (
        <FormField {...common}>
          {(f) => (
            <Select disabled={disabled ?? false} {...f}>
              <option value="">Choose…</option>
              {options.map((option) => (
                <option key={optionValue(option)} value={optionValue(option)}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      );
    case 'multiselect':
      return (
        <FormField {...common} control="group">
          {(f: FormFieldRenderProps<string[]>) => (
            <MultiSelect
              {...f}
              disabled={disabled ?? false}
              options={options.map((option) => ({ value: optionValue(option), label: option.label }))}
            />
          )}
        </FormField>
      );
    case 'rating':
      return (
        <FormField {...common} control="group">
          {(f: FormFieldRenderProps<number | undefined>) => (
            <Rating {...f} disabled={disabled ?? false} max={field.max ?? 5} />
          )}
        </FormField>
      );
    case 'date':
      return (
        <FormField {...common}>{(f) => <DateInput disabled={disabled ?? false} {...f} />}</FormField>
      );
    case 'datetime':
      return (
        <FormField {...common}>{(f) => <DateTimeInput disabled={disabled ?? false} {...f} />}</FormField>
      );
    case 'gps':
      return (
        <FormField {...common} control="group">
          {(f: FormFieldRenderProps<Partial<AddressValue> | undefined>) => (
            <AddressField {...f} disabled={disabled ?? false} />
          )}
        </FormField>
      );
    case 'text':
    default:
      return (
        <FormField {...common}>
          {(f) => <Input type="text" placeholder={field.placeholder ?? ''} disabled={disabled ?? false} {...f} />}
        </FormField>
      );
  }
}
